import { useEffect } from 'react';
import { useLocation, useOutletContext, useParams } from 'react-router-dom';
import { DocsMarkdown } from './components/docs-markdown';
import { DocsPager } from './components/docs-pager';
import { DEFAULT_DOC_SLUG } from './docs-nav-data';
import { SetDocsHeadings } from './docs-layout';
import { getDocBySlug } from './utils/load-docs';

export default function DocsPage() {
  const { slug } = useParams<{ slug: string }>();
  const { hash } = useLocation();
  const setHeadings = useOutletContext<SetDocsHeadings>();
  const doc = getDocBySlug(slug ?? DEFAULT_DOC_SLUG);

  useEffect(() => {
    setHeadings(doc?.headings ?? []);
  }, [doc, setHeadings]);

  // Router navigation doesn't scroll to #anchors, so search results that point at a section would land at the top.
  useEffect(() => {
    if (!hash) return;
    document.getElementById(decodeURIComponent(hash.slice(1)))?.scrollIntoView({ block: 'start' });
  }, [doc, hash]);

  if (!doc) {
    return (
      <div>
        <h1 className="text-2xl font-semibold text-foreground">Page not found</h1>
        <p className="mt-4 text-sm text-muted-foreground">
          We couldn&apos;t find a doc for &quot;{slug}&quot;.
        </p>
      </div>
    );
  }

  return (
    <article>
      <h1 className="text-3xl font-semibold text-foreground">{doc.frontmatter.title}</h1>
      {doc.frontmatter.description && (
        <p className="mt-3 text-base text-muted-foreground">{doc.frontmatter.description}</p>
      )}
      <DocsMarkdown content={doc.body} />
      <DocsPager slug={doc.frontmatter.slug} />
    </article>
  );
}
